import type { RateCatalogItem } from "../server/verify/types";

export function productFamily(product: string): string {
  const name = product.toLowerCase();
  if (/lite/.test(name)) return "lite";
  if (/great rate/.test(name)) return "great-rate";
  if (/simplifier/.test(name)) return "simplifier";
  if (/basic/.test(name)) return "basic";
  if (/budget/.test(name)) return "budget";
  return name.replace(/[^a-z0-9]+/g, " ").trim();
}

const IMB = "https://www.imb.com.au/home-loans/living-in/budget-home-loan";
const ING = "https://www.ing.com.au/rates-and-fees/home-loan-rates.html";
const MACQ = "https://www.macquarie.com.au/home-loans/home-loan-rates.html";

const imbCashback = 3000;

/** Only these URLs are fetched. This is not an internet crawl. Off-panel lenders are not in this list. */
export const RATE_CATALOG: RateCatalogItem[] = [
  {
    id: "ing-simplifier-oo-pi-80",
    lender: "ING",
    product: "Mortgage Simplifier 70-80%",
    occupancy: "owner",
    repayment: "pi",
    maxLvr: 80,
    minLvr: 70,
    minLoan: 150_000,
    lockedMaxLvr: 80,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: ING,
    parser: "ing",
    family: "simplifier",
    finePrint:
      "ING Mortgage Simplifier owner-occupier P&I at 70.01–80% LVR, min $150,000. Do not quote the 5.99% band on an 80% file. Not Orange Advantage (package/offset).",
  },
  {
    id: "macquarie-basic-oo-pi-80",
    lender: "Macquarie Bank",
    product: "Basic Home Loan 70-80%",
    occupancy: "owner",
    repayment: "pi",
    maxLvr: 80,
    minLvr: 70,
    lockedMaxLvr: 80,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: MACQ,
    parser: "macquarie",
    family: "basic",
    finePrint:
      "Macquarie Basic owner-occupier P&I at 70–80% LVR. Do not quote the 6.04% ≤70% band on an 80% file. Not the Offset product (higher comparison rate).",
  },
  {
    id: "imb-budget-oo-pi-70",
    lender: "IMB",
    product: "Budget Home Loan <=70%",
    occupancy: "owner",
    repayment: "pi",
    maxLvr: 70,
    lockedMaxLvr: 70,
    cashback: imbCashback,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint:
      "IMB Budget owner-occupier P&I at ≤70% LVR. There is no 5.89% Budget print. Cashback is IMB’s advertised offer — confirm the current amount.",
  },
  {
    id: "imb-budget-oo-pi-80",
    lender: "IMB",
    product: "Budget Home Loan 70-80%",
    occupancy: "owner",
    repayment: "pi",
    maxLvr: 80,
    minLvr: 70,
    lockedMaxLvr: 80,
    cashback: imbCashback,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget owner-occupier P&I at 70–80% LVR. Do not quote the ≤70% rate on this band.",
  },
  {
    id: "imb-budget-oo-pi-90",
    lender: "IMB",
    product: "Budget Home Loan 80-90%",
    occupancy: "owner",
    repayment: "pi",
    maxLvr: 90,
    minLvr: 80,
    lockedMaxLvr: 90,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget owner-occupier P&I at 80–90% LVR.",
  },
  {
    id: "imb-budget-oo-pi-95",
    lender: "IMB",
    product: "Budget Home Loan 90-95%",
    occupancy: "owner",
    repayment: "pi",
    maxLvr: 95,
    minLvr: 90,
    lockedMaxLvr: 95,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget owner-occupier P&I at 90–95% LVR.",
  },
  {
    id: "imb-budget-oo-io-70",
    lender: "IMB",
    product: "Budget Home Loan IO <=70%",
    occupancy: "owner",
    repayment: "io",
    maxLvr: 70,
    lockedMaxLvr: 70,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget owner-occupier interest-only at ≤70% LVR.",
  },
  {
    id: "imb-budget-oo-io-80",
    lender: "IMB",
    product: "Budget Home Loan IO 70-80%",
    occupancy: "owner",
    repayment: "io",
    maxLvr: 80,
    minLvr: 70,
    lockedMaxLvr: 80,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget owner-occupier interest-only at 70–80% LVR.",
  },
  {
    id: "imb-budget-inv-pi-80",
    lender: "IMB",
    product: "Budget Home Loan investment <=80%",
    occupancy: "investment",
    repayment: "pi",
    maxLvr: 80,
    lockedMaxLvr: 80,
    cashback: imbCashback,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget investment P&I at ≤80% LVR.",
  },
  {
    id: "imb-budget-inv-pi-90",
    lender: "IMB",
    product: "Budget Home Loan investment 80-90%",
    occupancy: "investment",
    repayment: "pi",
    maxLvr: 90,
    minLvr: 80,
    lockedMaxLvr: 90,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget investment P&I at 80–90% LVR.",
  },
  {
    id: "imb-budget-inv-io-80",
    lender: "IMB",
    product: "Budget Home Loan investment IO <=80%",
    occupancy: "investment",
    repayment: "io",
    maxLvr: 80,
    lockedMaxLvr: 80,
    cashback: 0,
    ongoingFee: 0,
    ratesUrl: IMB,
    parser: "imb",
    family: "budget",
    finePrint: "IMB Budget investment interest-only at ≤80% LVR.",
  },
];
